import { useState } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogBody, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { UserDisplay } from "@/components/user-display"
import { PROPERTY_TYPE_CONFIG, STATUS_CONFIG } from "@/lib/project-configs"
import { Check, ChevronsUpDown, X, Star } from "lucide-react"
import { cn } from "@/lib/utils"
import type { SharedProps, Project, ProjectTag, PipeStatusRecord, ProjectStatus, ProjectOrigin, PropertyType } from "@/types"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  users: { id: string; first_name: string; last_name: string }[]
  teams: { id: string; name: string }[]
  project_tags: ProjectTag[]
  pipeStatuses: PipeStatusRecord[]
  project?: Project
}

const PIPE_STATUS_GROUPS: { status: ProjectStatus; label: string }[] = [
  { status: "pending", label: STATUS_CONFIG.pending.label },
  { status: "accepted", label: STATUS_CONFIG.accepted.label },
  { status: "rejected", label: STATUS_CONFIG.rejected.label },
  { status: "asset", label: STATUS_CONFIG.asset.label },
  { status: "archived", label: STATUS_CONFIG.archived.label },
]

function getScoreColor(score: number) {
  if (score <= 3) return "#ef4444"
  if (score <= 6) return "#f59e0b"
  return "#22c55e"
}

export function ProjectFormDialog({ open, onOpenChange, users, teams, project_tags, pipeStatuses, project }: Props) {
  const { current_user } = usePage<SharedProps>().props
  const isEditing = !!project

  // Find default pipe_status_id (first "pending" status)
  const defaultPipeStatusId = project?.pipe_status_id || pipeStatuses.find((ps) => ps.parent_status === "pending")?.id || ""

  const { data, setData, post, patch, processing, reset } = useForm({
    name: project?.name || "",
    property_type: project?.property_type || "house",
    address: project?.address || "",
    zip_code: project?.zip_code || "",
    city_name: project?.city_name || "",
    city_code: project?.city_code || "",
    country: project?.country || "FR",
    email: project?.email || "",
    phone_number: project?.phone_number || "",
    pipe_status_id: defaultPipeStatusId,
    lead_score: project?.lead_score ?? 0,
    house_surface: project?.house_surface != null ? String(project.house_surface) : "",
    field_surface: project?.field_surface != null ? String(project.field_surface) : "",
    origin: project?.origin || "creation",
    organisation_id: String(project?.organisation_id || current_user?.organisation_id || ""),
    assigned_user_ids: (project?.assigned_users?.map((u) => u.id) || []) as string[],
    assigned_team_ids: (project?.assigned_teams?.map((t) => t.id) || []) as string[],
    project_tag_ids: (project?.project_tags?.map((t) => t.id) || []) as string[],
  })

  const [pipeStatusOpen, setPipeStatusOpen] = useState(false)
  const [usersOpen, setUsersOpen] = useState(false)
  const [teamsOpen, setTeamsOpen] = useState(false)
  const [tagsOpen, setTagsOpen] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const onSuccess = () => {
      onOpenChange(false)
      if (!isEditing) reset()
    }
    if (isEditing) {
      patch(`/projects/${project.id}`, { onSuccess })
    } else {
      post("/projects", { onSuccess })
    }
  }

  const toggleArrayItem = (field: "assigned_user_ids" | "assigned_team_ids" | "project_tag_ids", id: string) => {
    const current = data[field]
    setData(field, current.includes(id) ? current.filter((v) => v !== id) : [...current, id])
  }

  const selectedPipeStatus = pipeStatuses.find((ps) => ps.id === data.pipe_status_id)

  // Group pipe statuses by parent status
  const pipeStatusesByParent: Record<string, PipeStatusRecord[]> = {}
  for (const ps of pipeStatuses) {
    if (!pipeStatusesByParent[ps.parent_status]) pipeStatusesByParent[ps.parent_status] = []
    pipeStatusesByParent[ps.parent_status].push(ps)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Modifier le projet" : "Nouveau projet"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          <DialogBody className="space-y-5">
            {/* --- Informations --- */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">Informations</h4>
              <div className="space-y-2">
                <Label htmlFor="dlg_name">Nom du projet</Label>
                <Input id="dlg_name" value={data.name} onChange={(e) => setData("name", e.target.value)} required />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Statut pipeline</Label>
                  <Popover open={pipeStatusOpen} onOpenChange={setPipeStatusOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                        {selectedPipeStatus ? (
                          <span className="flex items-center gap-2">
                            <span
                              className="inline-block size-2.5 shrink-0 rounded-sm"
                              style={{ backgroundColor: selectedPipeStatus.text_color }}
                            />
                            {selectedPipeStatus.label}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Statut...</span>
                        )}
                        <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[250px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Rechercher..." />
                        <CommandList>
                          <CommandEmpty>Aucun résultat</CommandEmpty>
                          {PIPE_STATUS_GROUPS.map(({ status, label }) => {
                            const pipes = pipeStatusesByParent[status] || []
                            if (pipes.length === 0) return null
                            return (
                              <CommandGroup key={status} heading={label}>
                                {pipes.map((ps) => (
                                  <CommandItem
                                    key={ps.id}
                                    value={ps.label}
                                    onSelect={() => {
                                      setData("pipe_status_id", ps.id)
                                      setPipeStatusOpen(false)
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 size-4",
                                        data.pipe_status_id === ps.id ? "opacity-100" : "opacity-0",
                                      )}
                                    />
                                    <span
                                      className="inline-block size-2.5 shrink-0 rounded-sm"
                                      style={{ backgroundColor: ps.text_color }}
                                    />
                                    {ps.label}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )
                          })}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dlg_lead_score">Score (0-10)</Label>
                  <div className="relative">
                    <Star
                      className="absolute left-3 top-1/2 size-4 -translate-y-1/2"
                      style={{ color: getScoreColor(data.lead_score) }}
                    />
                    <Input
                      id="dlg_lead_score"
                      type="number"
                      min={0}
                      max={10}
                      value={data.lead_score}
                      onChange={(e) => setData("lead_score", parseInt(e.target.value) || 0)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dlg_origin">Origine</Label>
                  <Select value={data.origin} onValueChange={(v) => setData("origin", v as ProjectOrigin)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kaptcher">Kaptcher</SelectItem>
                      <SelectItem value="terrafinder">Terrafinder</SelectItem>
                      <SelectItem value="creation">Création manuelle</SelectItem>
                      <SelectItem value="other_origin">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* --- Localisation --- */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">Localisation</h4>
              <div className="space-y-2">
                <Label htmlFor="dlg_address">Adresse</Label>
                <Input id="dlg_address" value={data.address} onChange={(e) => setData("address", e.target.value)} />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="dlg_zip_code">Code postal</Label>
                  <Input
                    id="dlg_zip_code"
                    value={data.zip_code}
                    onChange={(e) => setData("zip_code", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dlg_city_name">Ville</Label>
                  <Input
                    id="dlg_city_name"
                    value={data.city_name}
                    onChange={(e) => setData("city_name", e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* --- Bien --- */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">Bien</h4>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="dlg_property_type">Type de bien</Label>
                  <Select value={data.property_type} onValueChange={(v) => setData("property_type", v as PropertyType)}>
                    <SelectTrigger>
                      <SelectValue>
                        {(() => {
                          const config = PROPERTY_TYPE_CONFIG[data.property_type]
                          if (!config) return null
                          const Icon = config.icon
                          return (
                            <span className="flex items-center gap-2">
                              <Icon className="size-4 text-muted-foreground" />
                              {config.label}
                            </span>
                          )
                        })()}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PROPERTY_TYPE_CONFIG).map(([value, config]) => {
                        const Icon = config.icon
                        return (
                          <SelectItem key={value} value={value}>
                            <span className="flex items-center gap-2">
                              <Icon className="size-4 text-muted-foreground" />
                              {config.label}
                            </span>
                          </SelectItem>
                        )
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dlg_house_surface">Surface bâtie (m²)</Label>
                  <Input
                    id="dlg_house_surface"
                    type="number"
                    step="0.01"
                    value={data.house_surface}
                    onChange={(e) => setData("house_surface", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dlg_field_surface">Surface terrain (m²)</Label>
                  <Input
                    id="dlg_field_surface"
                    type="number"
                    step="0.01"
                    value={data.field_surface}
                    onChange={(e) => setData("field_surface", e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* --- Contact --- */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">Contact</h4>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="dlg_email">Email</Label>
                  <Input
                    id="dlg_email"
                    type="email"
                    value={data.email}
                    onChange={(e) => setData("email", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dlg_phone">Téléphone</Label>
                  <Input
                    id="dlg_phone"
                    value={data.phone_number}
                    onChange={(e) => setData("phone_number", e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* --- Affectation --- */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">Affectation</h4>

              {/* Users - searchable combobox with avatars */}
              <div className="space-y-2">
                <Label>Utilisateurs</Label>
                <Popover open={usersOpen} onOpenChange={setUsersOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                      {data.assigned_user_ids.length > 0 ? (
                        <span className="text-sm">{data.assigned_user_ids.length} utilisateur(s) sélectionné(s)</span>
                      ) : (
                        <span className="text-muted-foreground">Sélectionner des utilisateurs...</span>
                      )}
                      <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[350px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Rechercher un utilisateur..." />
                      <CommandList>
                        <CommandEmpty>Aucun utilisateur trouvé</CommandEmpty>
                        <CommandGroup>
                          {users.map((u) => (
                            <CommandItem
                              key={u.id}
                              value={`${u.first_name} ${u.last_name}`}
                              onSelect={() => toggleArrayItem("assigned_user_ids", u.id)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 size-4 shrink-0",
                                  data.assigned_user_ids.includes(u.id) ? "opacity-100" : "opacity-0",
                                )}
                              />
                              <UserDisplay user={u} size="sm" />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {data.assigned_user_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {data.assigned_user_ids.map((id) => {
                      const u = users.find((u) => u.id === id)
                      if (!u) return null
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 rounded-md border bg-muted/50 px-2 py-0.5 text-sm"
                        >
                          <UserDisplay user={u} size="sm" avatarOnly />
                          <span>
                            {u.first_name} {u.last_name}
                          </span>
                          <button
                            type="button"
                            onClick={() => toggleArrayItem("assigned_user_ids", id)}
                            className="ml-0.5 rounded-sm hover:bg-muted"
                          >
                            <X className="size-3" />
                          </button>
                        </span>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Teams - searchable combobox */}
              <div className="space-y-2">
                <Label>Équipes</Label>
                <Popover open={teamsOpen} onOpenChange={setTeamsOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                      {data.assigned_team_ids.length > 0 ? (
                        <span className="text-sm">{data.assigned_team_ids.length} équipe(s) sélectionnée(s)</span>
                      ) : (
                        <span className="text-muted-foreground">Sélectionner des équipes...</span>
                      )}
                      <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Rechercher une équipe..." />
                      <CommandList>
                        <CommandEmpty>Aucune équipe trouvée</CommandEmpty>
                        <CommandGroup>
                          {teams.map((t) => (
                            <CommandItem
                              key={t.id}
                              value={t.name}
                              onSelect={() => toggleArrayItem("assigned_team_ids", t.id)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 size-4 shrink-0",
                                  data.assigned_team_ids.includes(t.id) ? "opacity-100" : "opacity-0",
                                )}
                              />
                              {t.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {data.assigned_team_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {data.assigned_team_ids.map((id) => {
                      const t = teams.find((t) => t.id === id)
                      if (!t) return null
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 rounded-md border bg-muted/50 px-2 py-0.5 text-sm"
                        >
                          {t.name}
                          <button
                            type="button"
                            onClick={() => toggleArrayItem("assigned_team_ids", id)}
                            className="ml-0.5 rounded-sm hover:bg-muted"
                          >
                            <X className="size-3" />
                          </button>
                        </span>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Tags - searchable combobox with colors */}
              <div className="space-y-2">
                <Label>Tags</Label>
                <Popover open={tagsOpen} onOpenChange={setTagsOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                      {data.project_tag_ids.length > 0 ? (
                        <span className="text-sm">{data.project_tag_ids.length} tag(s) sélectionné(s)</span>
                      ) : (
                        <span className="text-muted-foreground">Sélectionner des tags...</span>
                      )}
                      <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Rechercher un tag..." />
                      <CommandList>
                        <CommandEmpty>Aucun tag trouvé</CommandEmpty>
                        <CommandGroup>
                          {project_tags.map((tag) => (
                            <CommandItem
                              key={tag.id}
                              value={tag.name}
                              onSelect={() => toggleArrayItem("project_tag_ids", tag.id)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 size-4 shrink-0",
                                  data.project_tag_ids.includes(tag.id) ? "opacity-100" : "opacity-0",
                                )}
                              />
                              <span
                                className="inline-block size-3 shrink-0 rounded-sm"
                                style={{ backgroundColor: tag.color }}
                              />
                              {tag.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {data.project_tag_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {data.project_tag_ids.map((id) => {
                      const tag = project_tags.find((t) => t.id === id)
                      if (!tag) return null
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 rounded-md border-transparent px-2 py-0.5 text-sm text-white"
                          style={{ backgroundColor: tag.color }}
                        >
                          {tag.name}
                          <button
                            type="button"
                            onClick={() => toggleArrayItem("project_tag_ids", id)}
                            className="ml-0.5 rounded-sm hover:opacity-75"
                          >
                            <X className="size-3" />
                          </button>
                        </span>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={processing}>
              {isEditing ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
